import { Box, CardContent, Button } from "@mui/material";
import { EmailOutlined, Security } from "@mui/icons-material";
import { useState } from "react";
import { EmailOTP, GoogleAuthenticator } from "./TwoFAMethods";
import { useSetup2FA } from "../../../../../queries/2fa/setup2FA";
import { Setup2FARequestAuthTypeEnum } from "../../../../../services";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_ICON_COLOR,
  DEFAULT_KEY_LABEL_COLOR,
  DEFAULT_LABEL_COLOR,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  ICON_SIZE,
} from "../../../../../utils/utils";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import PasswordInput from "../../../../../components/app/PasswordInput";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";

// Interface for the API response data
interface TwoFASetupData {
  authType: string;
  message: string | null;
  secretKey: string;
  qrCodeUrl: string;
  qrCodeBase64: string;
  backupCodes: string[] | null;
}

const TwoFAauth = () => {
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [currentStep, setCurrentStep] = useState(1);
  const [password, setPassword] = useState("");
  const [showMethodSetup, setShowMethodSetup] = useState(false);
  const [apiData, setApiData] = useState<TwoFASetupData | null>(null);
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();

  const { mutateAsync: setup2FA } = useSetup2FA();

  const methods = [
    // {
    //   value: "SMS",
    //   title: "SMS OTP Verification",
    //   description: "Receive a one-time passcode via text message to your registered phone number.",
    //   icon: <ChatBubbleOutline sx={{ color: "#373D41", fontSize: 18 }} />,
    // },
    {
      value: "EMAIL",
      title: "Email OTP Verification",
      description:
        "Receive a one-time passcode via email to your registered email address.",
      icon: (
        <EmailOutlined
          sx={{ color: DEFAULT_ICON_COLOR, fontSize: ICON_SIZE }}
        />
      ),
    },
    {
      value: "GOOGLE_AUTHENTICATOR",
      title: "Google Authenticator (App-Based)",
      description:
        "Use the Google Authenticator app to generate time-based one-time codes.",
      icon: (
        <Security sx={{ color: DEFAULT_ICON_COLOR, fontSize: ICON_SIZE }} />
      ),
    },
  ];

  const handleMethodSelect = (methodValue: string) => {
    setSelectedMethod(methodValue);
    setCurrentStep(2);
    // console.log("Selected method:", methodValue);
  };

  const handleCancel = () => {
    setCurrentStep(1);
    setPassword("");
    setShowMethodSetup(false);
  };

  const handleContinue = () => {
    // console.log("Form submitted:", { selectedMethod, password });
    setup2FA({
      setup2FARequest: {
        authType: selectedMethod as Setup2FARequestAuthTypeEnum,
        password: password,
        providerId: me?.uuid ?? "",
      },
      xTENANTID: getXtenantId() ?? undefined,
    })
      .then((res) => {
        // console.log("res setup2FA", res);
        // Store the API data for Google Authenticator
        if (selectedMethod === "GOOGLE_AUTHENTICATOR" && res.data?.data) {
          setApiData(res.data.data as TwoFASetupData);
        }
        setShowMethodSetup(true);
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        // console.log("err setup2FA", err);
        showSnackbar(getErrorSnackbar(err));
      });
    // setShowMethodSetup(true);
  };

  // Render the specific method setup component
  if (showMethodSetup) {
    switch (selectedMethod) {
      // case "SMS":
      //   return <SMSOTP />;
      case "EMAIL":
        return <EmailOTP redirectToStart={handleCancel} />;
      case "GOOGLE_AUTHENTICATOR":
        return <GoogleAuthenticator apiData={apiData} />;
      default:
        return null;
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        padding: 2,
      }}
    >
      <OutlineCard
        sx={{ width: "100%", maxWidth: 700, backgroundColor: DEFAULT_BG_COLOR }}
      >
        <CardContent sx={{ padding: 5, textAlign: "center" }}>
          {currentStep === 1 ? (
            // Step 1: Method Selection
            <>
              <Text size={24}>2FA Security — Choose Verification Method</Text>

              <Text size={14} my={"20px"} color={DEFAULT_KEY_LABEL_COLOR}>
                Add an extra layer of security to your account. Choose how you'd
                like to receive verification codes when you sign in.
              </Text>

              <Box sx={{ width: "100%" }}>
                {methods.map((method) => (
                  <Box sx={{ marginBottom: 2, cursor: "pointer" }}>
                    <OutlineCard
                      key={method.value}
                      onClick={() => handleMethodSelect(method.value)}
                    >
                      <Box
                        sx={{ padding: 2, width: "100%", textAlign: "left" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            marginBottom: 0.75,
                          }}
                        >
                          {method.icon}
                          <Text size={14} fontWeight={400}>
                            {method.title}
                          </Text>
                        </Box>
                        <Text
                          size={14}
                          color={DEFAULT_KEY_LABEL_COLOR}
                          fontWeight={400}
                          lineHeight={1.6}
                          marginLeft={3.25}
                        >
                          {method.description}
                        </Text>
                      </Box>
                    </OutlineCard>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            // Step 2: Password Confirmation
            <>
              <Text
                size={16}
                fontWeight={500}
                color={DEFAULT_LABEL_COLOR}
                marginBottom={2}
              >
                Set Up Google Authenticator (Step 1 – Confirm Your Password)
              </Text>

              <Text
                size={14}
                color={DEFAULT_KEY_LABEL_COLOR}
                marginBottom={4}
                lineHeight={1.6}
              >
                Protect your account with app-based two-step verification.
                <br />
                You'll need the Google Authenticator app installed on your phone
                to continue.
              </Text>

              <OutlineCard
                sx={{
                  padding: 3,
                  border: "1px solid #E7E7E7",
                  borderRadius: 2,
                  backgroundColor: "#FFFFFF",
                  width: "100%",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "stretch",
                }}
              >
                <Text
                  size={14}
                  color={DEFAULT_KEY_LABEL_COLOR}
                  fontWeight={400}
                  marginBottom={1}
                  textAlign="left"
                >
                  For security, please re-enter your account password.
                </Text>
                <PasswordInput
                  value={password}
                  onChange={(v) => {
                    setPassword(v as string);
                  }}
                  required
                  error={false}
                />
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "stretch",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      flex: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!password.trim()}
                    sx={{
                      flex: 1,
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </OutlineCard>
            </>
          )}
        </CardContent>
      </OutlineCard>
    </Box>
  );
};

export default TwoFAauth;
